import React from "react";
import { Building, RefreshCw, AlertCircle, Phone, Mail } from "lucide-react";
import { Button } from "./ui/button";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for monitoring (you would integrate with Sentry here)
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // In production, you would send this to an error reporting service
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
          <div className="max-w-lg w-full text-center">
            {/* Company Branding */}
            <div className="mb-8">
              <div className="bg-blue-600 text-white p-4 rounded-2xl inline-block mb-4">
                <Building className="h-12 w-12" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">G.A Technology & Power City</h1>
              <p className="text-blue-600 font-medium">Rising with Innovation & Reliability</p>
            </div>

            {/* Error Message */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border">
              <div className="mb-6">
                <div className="bg-red-100 p-3 rounded-full inline-block mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h2>
                <p className="text-gray-600 leading-relaxed">
                  We apologize for the inconvenience. Our technical team has been notified 
                  and is working to resolve the issue.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mb-8">
                <Button 
                  onClick={this.handleReload}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  size="lg"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Page
                </Button>
                
                <Button 
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  <Building className="h-4 w-4 mr-2" />
                  Go to Homepage
                </Button>
              </div>

              {/* Contact Information */}
              <div className="border-t pt-6">
                <p className="text-sm text-gray-600 mb-4">Need immediate assistance?</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <a href="tel:+880255012345" className="text-blue-600 hover:underline">
                      +880-2-55012345 (Dhaka)
                    </a>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <a href="mailto:info@gatechnologypower.com" className="text-blue-600 hover:underline">
                      info@gatechnologypower.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Technical Details (Development Mode)
                </summary>
                <div className="mt-2 p-4 bg-gray-100 rounded-lg text-xs font-mono text-gray-700 overflow-auto">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  <div>
                    <strong>Component Stack:</strong>
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;